<script setup>
import { ref, computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownBody from 'dashboard/components-next/dropdown-menu/base/DropdownBody.vue';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  id: {
    type: [Number, null],
    default: null,
  },
  type: {
    type: String,
    required: true,
  },
  options: {
    type: Array,
    default: () => [],
  },
  activeFilterType: {
    type: String,
    default: '',
  },
  showMenu: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  enableSearch: {
    type: Boolean,
    default: false,
  },
  showClearFilter: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'toggleDropdown',
  'removeFilter',
  'addFilter',
  'closeDropdown',
]);

const searchTerm = ref('');

const toggleDropdown = () => emit('toggleDropdown', props.type);
const removeFilter = () => emit('removeFilter', props.type);
const addFilter = item => emit('addFilter', item);
const closeDropdown = () => {
  searchTerm.value = '';
  emit('closeDropdown');
};

const filteredListItems = computed(() => {
  if (!searchTerm.value) return props.options;
  return props.options.filter(opt =>
    opt.name.toLowerCase().includes(searchTerm.value.toLowerCase())
  );
});

const isFilterActive = optionId => {
  if (props.id === null || props.id === undefined) return false;
  return optionId.toString() === props.id.toString();
};
</script>

<template>
  <div class="relative">
    <NextButton
      outline
      slate
      sm
      no-animation
      icon="i-lucide-chevron-down"
      trailing-icon
      :label="name"
      @click="toggleDropdown"
    />
    <DropdownBody
      v-if="showMenu && activeFilterType === type"
      v-on-clickaway="closeDropdown"
      class="flex flex-col w-[240px] max-h-[400px] overflow-y-auto left-0 md:left-auto md:right-0 top-10 z-50"
    >
      <!-- Search input and Clear button -->
      <div
        v-if="enableSearch || showClearFilter"
        class="flex items-center justify-between gap-2 px-2 pb-2 pt-1 border-b border-n-weak bg-n-alpha-3 backdrop-blur-sm sticky top-0 z-10"
        @click.stop
      >
        <div v-if="enableSearch" class="flex items-center w-full gap-2">
          <span
            class="i-lucide-search size-3.5 text-n-slate-11 flex-shrink-0"
          />
          <input
            v-model="searchTerm"
            type="search"
            :placeholder="placeholder"
            class="reset-base w-full h-8 py-2 px-2 text-sm focus:outline-none border border-n-weak rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
            @click.stop
          />
        </div>
        <NextButton
          v-if="showClearFilter"
          faded
          xs
          class="flex-shrink-0"
          :label="$t('REPORT.FILTER_ACTIONS.CLEAR_FILTER')"
          @click="removeFilter"
        />
      </div>

      <!-- Option list items -->
      <div class="max-h-[300px] overflow-y-auto flex flex-col gap-0.5 mt-1">
        <div
          v-if="!filteredListItems.length"
          class="text-sm text-n-slate-11 px-2 py-1.5"
        >
          {{ $t('REPORT.FILTER_ACTIONS.EMPTY_LIST') }}
        </div>
        <button
          v-for="opt in filteredListItems"
          :key="opt.id"
          type="button"
          class="inline-flex items-center justify-start w-full h-8 px-2 py-1.5 text-sm rounded-lg hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2 text-n-slate-12 text-left gap-2"
          :class="{
            'bg-n-alpha-1 dark:bg-n-solid-active font-medium': isFilterActive(
              opt.id
            ),
          }"
          @click.stop="addFilter(opt)"
        >
          <span
            v-if="opt.icon"
            :class="opt.icon"
            class="flex-shrink-0 size-3.5"
          />
          <span class="truncate">{{ opt.name }}</span>
        </button>
      </div>
    </DropdownBody>
  </div>
</template>
